import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ShoppingBag, Truck, ShieldCheck, RefreshCw } from "lucide-react";
import { shopApi, fmtMoney } from "@/shop/shopApi";
import { useCart } from "@/shop/CartContext";
import ProductCard from "@/shop/ProductCard";
import { Reveal, easeOutExpo } from "@/shop/motion";

const RECENT_KEY = "sh_recent";

export default function ProductDetail() {
  const { handle } = useParams();
  const [state, setState] = useState({ loading: true, product: null, recs: [] });
  const [variant, setVariant] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [added, setAdded] = useState(false);
  const [recent, setRecent] = useState([]);
  const { addItem, busy } = useCart();

  useEffect(() => {
    setState({ loading: true, product: null, recs: [] });
    setActiveImg(0);
    shopApi.product(handle).then((d) => {
      setState({ loading: false, product: d.product, recs: d.recommendations || [] });
      const firstAvail = d.product.variants.find((v) => v.available) || d.product.variants[0];
      setVariant(firstAvail);
      // recently viewed
      try {
        const prev = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]").filter((x) => x.handle !== d.product.handle);
        const entry = { handle: d.product.handle, title: d.product.title, price: d.product.price, featured_image: d.product.featured_image, available: d.product.available };
        const next = [entry, ...prev].slice(0, 8);
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
        setRecent(prev.slice(0, 4));
      } catch (_) { /* ignore storage errors */ }
    }).catch(() => setState({ loading: false, product: null, recs: [] }));
  }, [handle]);

  const add = async () => {
    if (!variant) return;
    await addItem(variant.id, 1);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  if (state.loading) {
    return <div className="pt-28 pb-28 max-w-[1400px] mx-auto px-5 md:px-10"><div className="grid md:grid-cols-2 gap-12"><div className="aspect-square rounded-3xl bg-white/5 animate-pulse" /><div className="space-y-4"><div className="h-10 bg-white/5 rounded w-2/3 animate-pulse" /><div className="h-6 bg-white/5 rounded w-1/3 animate-pulse" /></div></div></div>;
  }
  if (!state.product) {
    return <div className="pt-40 pb-40 text-center"><p className="text-white/50">Product not found.</p><Link to="/shop/all" className="mt-6 inline-block text-sh-blue">← Back to shop</Link></div>;
  }

  const p = state.product;
  const images = p.images.length ? p.images : [p.featured_image].filter(Boolean);
  const displayImg = (variant?.image) || images[activeImg] || p.featured_image;
  const hasVariants = p.variants.length > 1;

  return (
    <div className="pt-24 pb-28" data-testid="product-detail-page">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Link to="/shop/all" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-8" data-testid="back-to-shop"><ArrowLeft className="w-4 h-4" /> Back to shop</Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Gallery */}
          <div>
            <motion.div key={displayImg} initial={{ opacity: 0.4, scale: 0.99 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: easeOutExpo }} className="relative aspect-square rounded-3xl overflow-hidden bg-[#161619]">
              {displayImg ? <img src={displayImg} alt={p.title} className="w-full h-full object-cover" data-testid="product-main-image" /> : <div className="w-full h-full grid place-items-center text-white/20">No image</div>}
            </motion.div>
            {images.length > 1 && (
              <div className="mt-4 flex gap-3 overflow-x-auto no-scrollbar">
                {images.map((img, i) => (
                  <button key={i} onClick={() => { setActiveImg(i); }} className={`w-20 h-20 rounded-xl overflow-hidden shrink-0 border-2 transition-colors ${activeImg === i ? "border-sh-blue" : "border-transparent opacity-60 hover:opacity-100"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:pt-4">
            {p.vendor && <p className="text-xs uppercase tracking-[0.25em] text-sh-blue mb-4">{p.vendor}</p>}
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-[-0.03em] leading-tight" data-testid="product-title">{p.title}</h1>
            <div className="mt-5 flex items-baseline gap-3">
              <span className="text-3xl font-semibold" data-testid="product-price">{fmtMoney(variant?.price || p.price)}</span>
              {variant?.compare_at_price && <span className="text-lg text-white/40 line-through">{fmtMoney(variant.compare_at_price)}</span>}
            </div>

            {hasVariants && (
              <div className="mt-8" data-testid="variant-selector">
                <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3">Options</p>
                <div className="flex flex-wrap gap-2.5">
                  {p.variants.map((v) => (
                    <button key={v.id} disabled={!v.available} onClick={() => setVariant(v)} data-testid={`variant-${v.id}`} className={`px-5 py-3 rounded-full text-sm border transition-colors ${variant?.id === v.id ? "border-sh-blue bg-sh-blue text-white" : "border-white/20 text-white/80 hover:border-white/50"} ${!v.available ? "opacity-35 line-through cursor-not-allowed" : ""}`}>{v.title}</button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8">
              <button onClick={add} disabled={busy || !variant?.available} data-testid="add-to-cart" className="w-full h-16 rounded-full bg-white text-black font-semibold text-[15px] flex items-center justify-center gap-2.5 hover:bg-sh-blue hover:text-white transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed">
                {added ? <><Check className="w-5 h-5" /> Added to cart</> : variant?.available ? <><ShoppingBag className="w-5 h-5" /> Add to cart</> : "Sold out"}
              </button>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3 text-center">
              {[[Truck, "Fast shipping"], [ShieldCheck, "Secure checkout"], [RefreshCw, "30-day returns"]].map(([Icon, label], i) => (
                <div key={i} className="border border-white/10 rounded-xl py-4 px-2">
                  <Icon className="w-5 h-5 text-sh-blue mx-auto" strokeWidth={1.4} />
                  <p className="text-[11px] text-white/55 mt-2">{label}</p>
                </div>
              ))}
            </div>

            {p.description_html ? (
              <div className="mt-10 pt-8 border-t border-white/10 text-white/70 leading-relaxed prose-invert text-[15px]" data-testid="product-description" dangerouslySetInnerHTML={{ __html: p.description_html }} />
            ) : p.description ? (
              <div className="mt-10 pt-8 border-t border-white/10 text-white/70 leading-relaxed whitespace-pre-line text-[15px]" data-testid="product-description">{p.description}</div>
            ) : null}
          </div>
        </div>

        {/* Recommendations */}
        {state.recs.length > 0 && (
          <section className="mt-28">
            <Reveal><h2 className="font-display text-3xl md:text-4xl font-bold tracking-[-0.03em] mb-10">You may also like</h2></Reveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="product-recommendations">
              {state.recs.map((r, i) => <ProductCard key={r.id} product={r} index={i} />)}
            </div>
          </section>
        )}

        {/* Recently viewed */}
        {recent.length > 0 && (
          <section className="mt-24">
            <h2 className="font-display text-2xl font-bold tracking-[-0.02em] mb-8 text-white/80">Recently viewed</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="recently-viewed">
              {recent.map((r, i) => <ProductCard key={r.handle} product={r} index={i} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
